<script setup lang="ts">
import { computed, h, onMounted, ref, watch, type VNode } from 'vue';
import { NEllipsis, NIcon, NPopover, useThemeVars, type DataTableColumns } from 'naive-ui';
import { IconDot } from '@/components/design/icons';
import type {
  ListPlaybookEvaluationsResponse,
  PlaybookEvaluationGroupInDb,
  PlaybookRunInDb,
  PlaybookRunStatus,
} from '@/api';
import {
  evaluationGroupBuckets,
  evaluationTypeBuckets,
  filterEvaluationColumns,
  runStatusType,
  showGroupFilterRow,
  showTypeFilterRow,
  usePlaybookRunCells,
} from '@/composables/usePlaybookRunCells';
import { usePlaybookRunWebSocket } from '@/composables/usePlaybookRunWebSocket';
import EvaluationColumnFilters from '@/components/playbooks/EvaluationColumnFilters.vue';
import EvaluationColumnPicker from '@/components/playbooks/EvaluationColumnPicker.vue';
import GaiaTable from '@/components/design/base/GaiaTable.vue';
import GaiaBanner from '@/components/design/composite/GaiaBanner.vue';
import { PlaybooksService } from '@/services/playbooks/playbooksService';
import { ReviewProjectService } from '@/services/reviewProjectService';
import { useReviewProjectStore, type AnalysisDataTableState } from '@/stores/reviewProjectStore';

// Deliberately separate from PlaybookRunsView's table: this one will grow its
// own interactions (cell clicks, context menus, …) while sharing the evaluation
// cell rendering via usePlaybookRunCells.
const props = defineProps<{
  projectUid: string;
  analysisUid: string;
  playbookUid: string;
  /** Bump to force a reload (e.g. after drag-dropped files started new runs). */
  refreshToken?: number;
}>();

const reviewProjectStore = useReviewProjectStore();
const themeVars = useThemeVars();

const runs = ref<PlaybookRunInDb[]>([]);
const evaluations = ref<ListPlaybookEvaluationsResponse>([]);
const evaluationGroups = ref<PlaybookEvaluationGroupInDb[]>([]);
const totalCount = ref<number>(0);
const loading = ref<boolean>(false);
const error = ref<string | null>(null);

// Pagination, selection and the column filters live in the store so they survive
// the pane being unmounted (the report expanding over it, a tab switch).
function createTableState(): AnalysisDataTableState {
  return { page: 1, pageSize: 50, columnFilter: { types: [], groups: [], hidden: [] } };
}
if (!(props.analysisUid in reviewProjectStore.analysisDataTableState)) {
  reviewProjectStore.analysisDataTableState[props.analysisUid] = createTableState();
}
if (!(props.analysisUid in reviewProjectStore.analysisSelectedRunUids)) {
  reviewProjectStore.analysisSelectedRunUids[props.analysisUid] = [];
}
const tableState = computed(
  () => reviewProjectStore.analysisDataTableState[props.analysisUid] ?? createTableState()
);

// Which evaluation columns are shown; an empty array on a dimension means "all".
const selectedTypes = computed<string[]>({
  get: () => tableState.value.columnFilter.types,
  set: (value): void => {
    tableState.value.columnFilter.types = value;
  },
});
const selectedGroups = computed<string[]>({
  get: () => tableState.value.columnFilter.groups,
  set: (value): void => {
    tableState.value.columnFilter.groups = value;
  },
});
const hiddenColumns = computed<string[]>({
  get: () => tableState.value.columnFilter.hidden ?? [],
  set: (value): void => {
    tableState.value.columnFilter.hidden = value;
  },
});
const typeBuckets = computed(() => evaluationTypeBuckets(evaluations.value));
const groupBuckets = computed(() =>
  evaluationGroupBuckets(evaluations.value, evaluationGroups.value)
);
const filterRowCount = computed<number>(
  () =>
    (showTypeFilterRow(typeBuckets.value) ? 1 : 0) +
    (showGroupFilterRow(groupBuckets.value) ? 1 : 0)
);
const visibleEvaluations = computed<ListPlaybookEvaluationsResponse>(() =>
  filterEvaluationColumns(evaluations.value, evaluationGroups.value, tableState.value.columnFilter)
);

// Clicking an evaluation result shows its data source: the run's input
// revision in the preview pane, jumped to the result's citation.
const { buildEvaluationColumnGroups } = usePlaybookRunCells(visibleEvaluations, {
  // A review project's analyses evaluate only — ReviewAnalysis starts every run
  // with every action type skipped, so there is no action state worth a cell.
  showActions: false,
  onResultClick: (run, _evaluation, result) => {
    const revisionUid = run.input_vault_file_revision?.uid;
    if (!revisionUid) return;
    reviewProjectStore.showCitation({
      revisionUid,
      page: result.page_number ?? null,
      highlights: result.highlighted_text ?? [],
    });
  },
});

async function loadRuns(): Promise<void> {
  loading.value = true;
  error.value = null;
  try {
    const result = await ReviewProjectService.searchAnalysisData(
      props.projectUid,
      props.analysisUid,
      { page: tableState.value.page, pageSize: tableState.value.pageSize }
    );
    runs.value = result.results;
    totalCount.value = result.result_count;
  } catch (error_) {
    error.value = error_ instanceof Error ? error_.message : 'Failed to load analysis data.';
  } finally {
    loading.value = false;
  }
}

/** Refetch a single run on a websocket update and patch it into the visible page. */
async function refreshRun(uid: string): Promise<void> {
  try {
    const updated = await PlaybooksService.getRun(uid);
    const index = runs.value.findIndex(run => run.uid === uid);
    if (index !== -1) {
      runs.value.splice(index, 1, updated);
    }
  } catch {
    // Ignore transient refetch errors; the next event or reload will recover.
  }
}

usePlaybookRunWebSocket({
  runUids: () => runs.value.map(run => run.uid),
  onRunUpdated: uid => {
    void refreshRun(uid);
  },
  playbookUid: () => props.playbookUid,
  onRunCreated: () => void loadRuns(),
});

async function loadEvaluations(): Promise<void> {
  try {
    evaluations.value = await PlaybooksService.listEvaluations(props.playbookUid);
  } catch {
    evaluations.value = [];
  }
}

// Only used to offer the group filter; failing to load just hides that row.
async function loadEvaluationGroups(): Promise<void> {
  try {
    evaluationGroups.value = await PlaybooksService.listEvaluationGroups(props.playbookUid);
  } catch {
    evaluationGroups.value = [];
  }
}

onMounted(() => {
  void loadEvaluations();
  void loadEvaluationGroups();
  void loadRuns();
});

watch(
  () => props.refreshToken,
  () => {
    void loadRuns();
  }
);

function handlePageChange(page: number): void {
  tableState.value.page = page;
  void loadRuns();
}

function handlePageSizeChange(size: number): void {
  tableState.value.pageSize = size;
  tableState.value.page = 1;
  void loadRuns();
}

const paginationConfig = computed(() => ({
  page: tableState.value.page,
  pageSize: tableState.value.pageSize,
  itemCount: totalCount.value,
  showSizePicker: true,
  pageSizes: [10, 25, 50, 100],
  onUpdatePage: handlePageChange,
  onUpdatePageSize: handlePageSizeChange,
}));

// Selection is per analysis and independent of the data room; it exists for
// the future chat pane. Naive keeps checked keys that aren't in the current
// remote page, so cross-page selections survive the plain assignment.
function handleCheckedRowKeys(keys: (string | number)[]): void {
  reviewProjectStore.analysisSelectedRunUids[props.analysisUid] = keys.map(String);
}

// The tag-type palette keyed by run status, resolved to actual theme colors.
function runStatusColor(status: PlaybookRunStatus): string {
  const colorByTagType: Record<string, string> = {
    success: themeVars.value.successColor,
    info: themeVars.value.infoColor,
    warning: themeVars.value.warningColor,
    error: themeVars.value.errorColor,
  };
  return colorByTagType[runStatusType[status]] ?? themeVars.value.textColor3;
}

const columns = computed<DataTableColumns<PlaybookRunInDb>>(() => [
  { type: 'selection', fixed: 'left' },
  {
    // A colored dot instead of a tag to save width; the string shows on hover.
    title: '',
    key: 'status',
    width: 40,
    align: 'center',
    fixed: 'left',
    render: (row): VNode =>
      h(
        NPopover,
        { trigger: 'hover', placement: 'top' },
        {
          trigger: () =>
            h(
              NIcon,
              { size: 12, color: runStatusColor(row.status), style: 'cursor: default;' },
              { default: () => h(IconDot) }
            ),
          default: () => row.status,
        }
      ),
  },
  {
    title: 'File',
    key: 'file',
    width: 180,
    fixed: 'left',
    render: (row): VNode =>
      h(
        NEllipsis,
        { tooltip: true, style: 'max-width: 160px;' },
        { default: () => row.input_vault_file_revision?.file_name ?? '—' }
      ),
  },
  ...buildEvaluationColumnGroups(),
]);

const scrollX = computed<number>(() => 260 + visibleEvaluations.value.length * 200);
</script>

<template>
  <!-- The pane insets its own body, so nothing here restates that edge. -->
  <gaia-banner v-if="error" type="error" style="margin: 12px 0">
    {{ error }}
  </gaia-banner>
  <n-flex v-else vertical :size="8" style="height: 100%">
    <n-flex v-if="evaluations.length" align="flex-end" :size="12">
      <EvaluationColumnFilters
        v-if="filterRowCount > 0"
        v-model:types="selectedTypes"
        v-model:groups="selectedGroups"
        :type-buckets="typeBuckets"
        :group-buckets="groupBuckets"
        :total="evaluations.length"
        :shown="visibleEvaluations.length"
        :style="{ flex: 1, minWidth: 0 }"
      />
      <evaluation-column-picker
        v-model:hidden="hiddenColumns"
        :evaluations="evaluations"
        :style="{ flex: 'none', marginLeft: 'auto' }"
      />
    </n-flex>
    <!-- GaiaTable rather than a bare n-data-table: the global Pagination theme
         fills the current page solid ink, and the wrapper is where that is
         corrected to the selected-but-not-primary treatment (along with the
         uppercase header casing every other table in the app has). It brings
         its card chrome with it, which the mock draws flush — the deviation is
         accepted in exchange for the grid matching the rest of the system. -->
    <div style="flex: 1; min-height: 0">
      <gaia-table
        :columns="columns"
        :data="runs"
        :loading="loading"
        :pagination="paginationConfig"
        :remote="true"
        :scroll-x="scrollX"
        :row-key="(row: PlaybookRunInDb) => row.uid"
        :checked-row-keys="reviewProjectStore.analysisSelectedRunUids[analysisUid]"
        flex-height
        class="analysis-data-table"
        :style="{ height: '100%', '--eval-group-divider': themeVars.dividerColor }"
        @update:checked-row-keys="handleCheckedRowKeys"
      />
    </div>
  </n-flex>
</template>

<style scoped>
/* Vertical divider marking where each evaluation-type group starts. */
.analysis-data-table :deep(th.eval-group-start),
.analysis-data-table :deep(td.eval-group-start) {
  border-left: 1px solid var(--eval-group-divider);
}
</style>
